import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { H3, paletteHelper, spacingHelper, Text, typographyHelper } from '@property.com.au/ui-kit';
import { DialogModal } from '../../../../../../../app-kit/components/DialogModal/DialogModal';
import { OutboundLink } from '../../../../../../../app-kit/components/OutboundLink/OutboundLink';

interface EnergyEfficiencyDisclaimerProps {
  opened: boolean;
  onClose: () => void;
}

const BodyText = styled(Text)`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textSecondary')};
`;

const DialogHeading = styled(H3)`
  ${typographyHelper('subtitle01')}
  color: ${paletteHelper('textSecondary')};
`;

const EnergyEfficiencyDisclaimerContentContainer = styled.article`
  ${BodyText}:not(:last-child) {
    margin-bottom: ${spacingHelper('large')};
  }
`;

const StyledOutboundLink = styled(OutboundLink)`
  color: ${paletteHelper('linkPrimary')};
  &:hover {
    color: ${paletteHelper('linkPrimary')};
  }
  text-decoration: none;
`;

const ReaSiteLink: FunctionComponent = () => {
  return (
    <StyledOutboundLink
      href="https://realestate.com.au"
      outboundLinkType="rea-site-outbound-link"
      variant="inline"
      rel="noopener noreferrer"
    >
      realestate.com.au
    </StyledOutboundLink>
  );
};

const ACTPlanningAndLandAuthorityLink: FunctionComponent = () => {
  return (
    <StyledOutboundLink
      href="https://www.planning.act.gov.au/"
      outboundLinkType="act-planning-and-land-authority-outbound-link"
      variant="inline"
      rel="noopener noreferrer"
    >
      ACT Planning & Land Authority
    </StyledOutboundLink>
  );
};

export const EnergyEfficiencyDisclaimerContent: FunctionComponent = () => {
  return (
    <EnergyEfficiencyDisclaimerContentContainer>
      <BodyText>
        This information is collected from the most recent listing of the property on{' '}
        <ReaSiteLink /> and should be used for reference purposes only.
      </BodyText>

      <BodyText>
        Property.com.au makes no representations or warranties regarding the accuracy of this data.
        You should always consult the relevant government body or original source to verify
        information before relying on it.
      </BodyText>

      <DialogHeading>For ACT properties</DialogHeading>
      <BodyText>
        Australian Capital Territory (ACT) residential property advertising must specify the
        assessed Energy Efficiency Rating (EER) for any dwelling being sold. More energy efficiency
        for house sales information is available at <ACTPlanningAndLandAuthorityLink />.
      </BodyText>
    </EnergyEfficiencyDisclaimerContentContainer>
  );
};

export const EnergyEfficiencyDisclaimer: FunctionComponent<EnergyEfficiencyDisclaimerProps> = ({
  opened,
  onClose,
}) => {
  return (
    <DialogModal
      opened={opened}
      onClose={onClose}
      title="Energy efficiency rating (EER)"
      primaryButtonText="Got it"
      primaryButtonOnClick={onClose}
      gap="0"
    >
      <EnergyEfficiencyDisclaimerContent />
    </DialogModal>
  );
};
